import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  BackLink,
  Button,
  Field,
  Input,
  Modal,
  Notice,
  PageHead,
  Panel,
  Select,
  Spinner,
  StatBox,
  StatRow,
  Table,
  TableWrap,
  Textarea,
  Toolbar,
  useToast,
} from 'iboon-ui';

import { api, type Movement, type Person, type StockItem } from '../api';
import { useIBoonAIContext } from '../iboonAi';

type Holder = { id: string; name: string; email: string; holding: number };

/**
 * One kind of thing: what there is, who has some, and every change.
 *
 * The movement list is not an audit trail bolted on the side -- it *is* the
 * data. The five numbers above it are worked out from these rows every time
 * the page loads, so a movement is the only way any of them ever changes.
 */
const KINDS = [
  { id: 'received', label: 'Received some' },
  { id: 'issued', label: 'Gave some out' },
  { id: 'returned', label: 'Got some back' },
  { id: 'defective', label: 'Found some broken' },
  { id: 'repaired', label: 'Fixed some' },
  { id: 'written_off', label: 'Threw some away' },
] as const;

const when = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });

export function StockDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState<StockItem | null>(null);
  const [movements, setMovements] = useState<Movement[]>([]);
  const [holders, setHolders] = useState<Holder[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  useIBoonAIContext(
    'stockItem',
    item && {
      id: item.id,
      name: item.name,
      categoryName: item.categoryName,
      locationName: item.locationName,
      total: item.total,
      available: item.available,
      assigned: item.assigned,
      defective: item.defective,
      reorderLevel: item.reorderLevel,
    },
  );

  const load = useCallback(async () => {
    const result = await api.get<{ item: StockItem; movements: Movement[]; holders: Holder[] }>(
      `/api/stock/${id}`,
    );
    setItem(result.item);
    setMovements(result.movements);
    setHolders(result.holders);
  }, [id]);

  useEffect(() => {
    load().catch((caught: unknown) =>
      setError(caught instanceof Error ? caught.message : 'Could not load it.'),
    );
    api.get<{ people: Person[] }>('/api/people').then((r) => setPeople(r.people.filter((p) => p.active)));
  }, [load]);

  if (error) {
    return (
      <Notice tone="danger" title="Could not load that item">
        {error}
      </Notice>
    );
  }
  if (!item) return <Spinner />;

  return (
    <>
      <BackLink onClick={() => navigate('/stock')}>Back to stock</BackLink>

      <PageHead
        title={item.name}
        subtitle={[item.make, item.model].filter(Boolean).join(' ') || item.categoryName}
        actions={
          <Button variant="primary" onClick={() => setRecording(true)}>
            Record a change
          </Button>
        }
      />

      {/* Worked out from the movements below, never stored. */}
      <StatRow>
        <StatBox value={item.total} label="We have" />
        <StatBox value={item.available} label="Free" />
        <StatBox value={item.assigned} label="Out with people" />
        <StatBox value={item.working} label="Working" />
        <StatBox value={item.defective} label="Broken" />
      </StatRow>

      <Panel title="Who has one">
        {holders.length === 0 ? (
          <p className="muted">Nobody.</p>
        ) : (
          holders.map((holder) => (
            <div key={holder.id} className="line">
              <span>
                {holder.name}
                <div className="sub">{holder.email}</div>
              </span>
              <span className="num">{holder.holding}</span>
            </div>
          ))
        )}
      </Panel>

      <Panel title="Everything that has happened">
        {movements.length === 0 ? (
          <p className="muted">
            Nothing yet. Record what you have with a “received” change, and the numbers above
            follow.
          </p>
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>What</th>
                  <th className="num">How many</th>
                  <th>Who</th>
                  <th>Recorded by</th>
                </tr>
              </thead>
              <tbody>
                {movements.map((movement) => (
                  <tr key={movement.id}>
                    <td className="muted">{when(movement.at)}</td>
                    <td>{KINDS.find((k) => k.id === movement.kind)?.label ?? movement.kind}</td>
                    <td className="num">{movement.quantity}</td>
                    <td>{movement.personName ?? <span className="muted">—</span>}</td>
                    <td className="muted">{movement.recordedBy || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Panel>

      {recording && (
        <Record
          item={item}
          people={people}
          onClose={() => setRecording(false)}
          onDone={() => {
            setRecording(false);
            void load();
          }}
        />
      )}
    </>
  );
}

function Record({
  item,
  people,
  onClose,
  onDone,
}: {
  item: StockItem;
  people: Person[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [kind, setKind] = useState<string>('received');
  const [quantity, setQuantity] = useState('1');
  const [personId, setPersonId] = useState(people[0]?.id ?? '');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const needsPerson = kind === 'issued' || kind === 'returned';

  async function save() {
    setBusy(true);
    try {
      await api.post(`/api/stock/${item.id}/movements`, {
        kind,
        quantity: Number(quantity),
        ...(needsPerson ? { personId } : {}),
        notes,
      });
      toast.show('Recorded.');
      onDone();
    } catch (caught) {
      // The server refuses for real reasons and words them with the numbers
      // in: "there are not that many to issue, 7 available". Passed through.
      toast.show(caught instanceof Error ? caught.message : 'That did not work.', { tone: 'danger' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open size="sm" title={`${item.name} — record a change`} onClose={onClose}>
      <Field label="What happened" required>
        <Select value={kind} onChange={(event) => setKind(event.target.value)}>
          {KINDS.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </Select>
      </Field>

      <Field label="How many" required>
        <Input
          type="number"
          min={1}
          value={quantity}
          onChange={(event) => setQuantity(event.target.value)}
        />
      </Field>

      {needsPerson &&
        (people.length === 0 ? (
          <Notice tone="accent" title="Nobody to give it to">
            The directory is empty. Sync it from identity on the People page first.
          </Notice>
        ) : (
          <Field label={kind === 'issued' ? 'Who is taking them' : 'Who is giving them back'} required>
            <Select value={personId} onChange={(event) => setPersonId(event.target.value)}>
              {people.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.name} — {person.email}
                </option>
              ))}
            </Select>
          </Field>
        ))}

      <Field label="Anything worth noting">
        <Textarea rows={2} value={notes} onChange={(event) => setNotes(event.target.value)} />
      </Field>

      <div className="row-actions" style={{ marginTop: 14, justifyContent: 'flex-start' }}>
        <Button
          variant="primary"
          disabled={busy || Number(quantity) < 1 || (needsPerson && !personId)}
          onClick={() => void save()}
        >
          {busy ? 'Recording…' : 'Record it'}
        </Button>
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
}
